import memoize from 'lodash/memoize';

import { DesignData } from 'editor/src/store/design/types';
import { DEFAULT_LOW_DPI, DEFAULT_MEDIUM_DPI } from 'editor/src/store/hostSettings/slice';
import { DpiLevels, PrintTechRules } from 'editor/src/store/hostSettings/types';
import shouldDigitizeSpread from 'editor/src/store/utils/shouldDigitizeSpread';

// One level for the whole design, so the badge, the warning and the upload hint agree: the
// strictest partner minimum among the technologies the design prints with.
function getPartnerMinDpi(designData: DesignData | undefined, printTechRules: PrintTechRules | undefined): number {
  if (!printTechRules || !designData) {
    return 0;
  }
  return designData.spreads.reduce((minDpi, spread) => {
    if (!spread.technology || shouldDigitizeSpread(spread)) {
      return minDpi;
    }
    return Math.max(minDpi, printTechRules[spread.technology]?.minDpi ?? 0);
  }, 0);
}

const getDpiLevelsFromDesign = (designData: DesignData | undefined, partnerMinDpi: number): DpiLevels => {
  const restrictions = designData?.resolution?.dpi;
  const low = restrictions?.min || DEFAULT_LOW_DPI;
  const medium = restrictions?.ok || DEFAULT_MEDIUM_DPI;
  // A product's own levels stay as they are even when its medium sits below its low.
  if (partnerMinDpi <= low) {
    return { low, medium };
  }
  return { low: partnerMinDpi, medium: Math.max(medium, partnerMinDpi) };
};

const getDpiLevelsMemoized = memoize(getDpiLevelsFromDesign, (designData, partnerMinDpi) => {
  const restrictions = designData?.resolution?.dpi;
  return `${restrictions?.min}:${restrictions?.ok}:${partnerMinDpi}`;
});

const getDpiLevelsFromDesignMemoized = (designData: DesignData | undefined, printTechRules?: PrintTechRules) =>
  getDpiLevelsMemoized(designData, getPartnerMinDpi(designData, printTechRules));

export default getDpiLevelsFromDesignMemoized;
